import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import { format } from 'date-fns';
import axios from 'axios';

const FoundationSchoolDetail = () => {
  const { id, organizationSlug } = useParams();
  const navigate = useNavigate();
  const { organizationSlug: authOrgSlug } = useAuth();
  const currentOrgSlug = organizationSlug || authOrgSlug;
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setStudent(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching foundation school student:', err);
        setError(err.response?.data?.error || 'Failed to fetch student details');
        setLoading(false);
      }
    };

    if (id) {
      fetchStudent();
    }
  }, [id]);

  const handleDownloadCertificate = async () => {
    try {
      setDownloadingCertificate(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/${id}/certificate`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Foundation-School-Certificate-${student.first_name}-${student.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbarMessage('Certificate downloaded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDownloadingCertificate(false);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate certificate';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDownloadingCertificate(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setSnackbarMessage('Student deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        const path = currentOrgSlug 
          ? `/${currentOrgSlug}/foundation-school-dashboard`
          : '/foundation-school-dashboard';
        navigate(path);
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete student');
      setSnackbarMessage('Failed to delete student');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
      case 'assigned':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleBack = () => {
    const path = currentOrgSlug 
      ? `/${currentOrgSlug}/foundation-school-dashboard`
      : '/foundation-school-dashboard';
    navigate(path);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !student) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!student) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: '#DC2626' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ 
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              {student.first_name} {student.last_name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Foundation School Student Details
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <Chip 
            label={student.status} 
            color={getStatusColor(student.status)} 
            sx={{ fontWeight: 'bold' }}
          />
          {student.assigned_class === 'class_2' && (
            <Button
              variant="contained"
              startIcon={downloadingCertificate ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
              onClick={handleDownloadCertificate}
              disabled={downloadingCertificate}
              sx={{
                background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)',
                }
              }}
            >
              {downloadingCertificate ? 'Downloading...' : 'Download Certificate'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: '#DC2626' }} />
                <Typography variant="h6">Personal Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Full Name"
                    secondary={`${student.first_name} ${student.last_name}`}
                  />
                </ListItem>
                {student.date_of_birth && (
                  <ListItem>
                    <ListItemText 
                      primary="Date of Birth"
                      secondary={format(new Date(student.date_of_birth), 'MMMM dd, yyyy')}
                    />
                  </ListItem>
                )}
                {student.gender && (
                  <ListItem>
                    <ListItemText 
                      primary="Gender"
                      secondary={student.gender}
                    />
                  </ListItem>
                )}
                {student.marital_status && (
                  <ListItem>
                    <ListItemText 
                      primary="Marital Status"
                      secondary={student.marital_status}
                    />
                  </ListItem>
                )}
                {student.occupation && (
                  <ListItem>
                    <ListItemText 
                      primary="Occupation"
                      secondary={student.occupation}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ mr: 1, color: '#DC2626' }} />
                <Typography variant="h6">Contact Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                {student.phone_number && (
                  <ListItem>
                    <ListItemText 
                      primary="Phone Number"
                      secondary={student.phone_number}
                    />
                  </ListItem>
                )}
                {student.email && (
                  <ListItem>
                    <ListItemText 
                      primary="Email"
                      secondary={student.email}
                    />
                  </ListItem>
                )}
                {(student.street_address || student.city || student.postal_code) && (
                  <ListItem>
                    <ListItemText 
                      primary="Address"
                      secondary={
                        <Box>
                          {student.street_address && <Typography variant="body2">{student.street_address}</Typography>}
                          {(student.city || student.postal_code) && (
                            <Typography variant="body2">
                              {student.city}{student.postal_code ? `, ${student.postal_code}` : ''}
                            </Typography>
                          )}
                          {student.state && <Typography variant="body2">{student.state}</Typography>}
                          {student.country && <Typography variant="body2">{student.country}</Typography>}
                        </Box>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Enrollment Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: '#DC2626' }} />
                <Typography variant="h6">Enrollment Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Enrollment Date"
                    secondary={format(new Date(student.enrollment_date), 'MMMM dd, yyyy')}
                  />
                </ListItem>
                {student.assigned_class && (
                  <ListItem>
                    <ListItemText 
                      primary="Assigned Class"
                      secondary={
                        <Chip 
                          label={student.assigned_class === 'class_1' ? 'Class 1' : 'Class 2'} 
                          color="primary"
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                )}
                {student.certificate_number && (
                  <ListItem>
                    <ListItemText 
                      primary="Certificate Number"
                      secondary={student.certificate_number}
                    />
                  </ListItem>
                )}
                {student.assigned_class === 'class_1' && (
                  <ListItem>
                    <ListItemText 
                      primary="Completion Status"
                      secondary={
                        <Chip
                          label="Class 2 Pending"
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      }
                    />
                  </ListItem>
                )}
                {student.assigned_class === 'class_2' && (
                  <ListItem>
                    <ListItemText 
                      primary="Completion Status"
                      secondary={
                        <Chip
                          label="Completed"
                          color="success"
                          size="small"
                          variant="filled"
                        />
                      }
                    />
                  </ListItem>
                )}
                {student.class_1_completed && student.class_1_completion_date && (
                  <ListItem>
                    <ListItemText 
                      primary="Class 1 Completed"
                      secondary={format(new Date(student.class_1_completion_date), 'MMMM dd, yyyy')}
                    />
                  </ListItem>
                )}
                {student.class_2_completed && student.class_2_completion_date && (
                  <ListItem>
                    <ListItemText 
                      primary="Class 2 Completed"
                      secondary={format(new Date(student.class_2_completion_date), 'MMMM dd, yyyy')}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Foundation School Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: '#DC2626' }} />
                <Typography variant="h6">Foundation School Details</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                {student.baptized_in_holy_ghost && (
                  <ListItem>
                    <ListItemText 
                      primary="Baptized in Holy Ghost"
                      secondary={student.baptized_in_holy_ghost}
                    />
                  </ListItem>
                )}
                {student.class_taken && !student.assigned_class && (
                  <ListItem>
                    <ListItemText 
                      primary="Class Preference"
                      secondary={`${student.class_taken} (Indicated during enrollment)`}
                    />
                  </ListItem>
                )}
                {student.class_taken && student.assigned_class && (
                  <ListItem>
                    <ListItemText 
                      primary="Class Taken (Enrollment)"
                      secondary={student.class_taken}
                    />
                  </ListItem>
                )}
                {student.reason_for_enrollment && (
                  <ListItem>
                    <ListItemText 
                      primary="Reason for Enrollment"
                      secondary={student.reason_for_enrollment}
                    />
                  </ListItem>
                )}
                {student.expectations && (
                  <ListItem>
                    <ListItemText 
                      primary="Expectations"
                      secondary={student.expectations}
                    />
                  </ListItem>
                )}
                {student.feedback && (
                  <ListItem>
                    <ListItemText 
                      primary="Feedback"
                      secondary={student.feedback}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {student.first_name} {student.last_name}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FoundationSchoolDetail;

